import { Outlet, Link, createFileRoute, useLocation } from "@tanstack/react-router";
import {
  LayoutDashboard,
  Home,
  BedDouble,
  CalendarRange,
  ShieldAlert,
  CalendarClock,
} from "lucide-react";
import { useAuth } from "@/lib/auth-context";
import { PageShell } from "@/components/nav";

export const Route = createFileRoute("/_authenticated/host")({
  component: HostLayout,
});

type Tab = {
  to: "/host" | "/host/properties" | "/host/listings" | "/host/bookings" | "/host/calendar";
  label: string;
  icon: typeof LayoutDashboard;
  exact?: boolean;
};

const tabs: Tab[] = [
  { to: "/host", label: "Overview", icon: LayoutDashboard, exact: true },
  { to: "/host/calendar", label: "Calendar", icon: CalendarClock },
  { to: "/host/properties", label: "Properties", icon: Home },
  { to: "/host/listings", label: "Listings", icon: BedDouble },
  { to: "/host/bookings", label: "Bookings", icon: CalendarRange },
];

function HostLayout() {
  const { user } = useAuth();
  const location = useLocation();

  if (!user) return null;

  /*
    Both gates now point somewhere. The first used to say "contact support",
    which was the only honest thing to say when no application flow existed;
    the second promised that adding a property would get it verified, and
    nothing in the system could ever verify one.
  */
  if (user.role !== "host") {
    return (
      <PageShell>
        <Gate
          title="Hosting starts with a quick check"
          message="We verify every host before they can take bookings. It takes a few minutes and one photo ID."
          cta={
            <Link
              to="/become-host"
              className="inline-flex items-center justify-center rounded-xl bg-primary px-4 py-2.5 text-sm font-bold text-primary-foreground hover:opacity-90"
            >
              Apply to become a host
            </Link>
          }
        />
      </PageShell>
    );
  }

  if (user.can_access_host_dashboard === false) {
    return (
      <PageShell>
        <Gate
          title="Your application is still open"
          message="Your host tools unlock as soon as your application is approved. Check where it stands or add anything we asked for."
          cta={
            <Link
              to="/become-host"
              className="inline-flex items-center justify-center rounded-xl bg-primary px-4 py-2.5 text-sm font-bold text-primary-foreground hover:opacity-90"
            >
              View my application
            </Link>
          }
        />
      </PageShell>
    );
  }

  return (
    <PageShell>
      <div className="border-b border-border bg-card/50">
        <div className="mx-auto flex max-w-6xl gap-1 overflow-x-auto px-4 py-2">
          {tabs.map((tab) => {
            const active = tab.exact
              ? location.pathname === tab.to
              : location.pathname.startsWith(tab.to);
            const Icon = tab.icon;
            return (
              <Link
                key={tab.to}
                to={tab.to}
                className={`inline-flex items-center gap-2 whitespace-nowrap rounded-lg px-3 py-2 text-sm font-semibold transition-colors ${
                  active
                    ? "bg-primary text-primary-foreground"
                    : "text-ink-muted hover:bg-muted hover:text-ink"
                }`}
              >
                <Icon className="h-4 w-4" />
                {tab.label}
              </Link>
            );
          })}
        </div>
      </div>
      <Outlet />
    </PageShell>
  );
}

function Gate({ title, message, cta }: { title: string; message: string; cta?: React.ReactNode }) {
  return (
    <div className="mx-auto max-w-xl px-4 py-16 text-center">
      <div className="mx-auto grid h-14 w-14 place-items-center rounded-2xl bg-accent/40 text-secondary">
        <ShieldAlert className="h-7 w-7" />
      </div>
      <h1 className="mt-4 text-2xl font-extrabold tracking-tight text-ink">{title}</h1>
      <p className="mt-2 text-sm text-ink-muted">{message}</p>
      {cta && <div className="mt-6">{cta}</div>}
    </div>
  );
}
